// TCIO Call Coaching — product redesign 2026-09-01 (brief + A/B/C/D table ratified in-session)
// Phase 1: real data only. api 473ea1e supplies windowed aggregates, trends, Faith split,
// journey, notes. No invented numbers anywhere; sections without data render honestly or not at all.
import React, { useState, useEffect, useCallback } from 'react';
import { tokens as tk } from './theme';
import {
  AudioLines, Hand, MessagesSquare, UserRound, ShieldCheck, Flag as FlagIcon,
  HeartHandshake, Sparkles, TrendingUp, TrendingDown, Target, BookOpen,
  ChevronDown, ChevronRight, Search, Bot, X, Play, StickyNote,
} from 'lucide-react';

const API = 'https://tcio-api.onrender.com';
const DOT = '\u00B7';
const DASH = '\u2014';
const ELLIP = '\u2026';
const TIMES = '\u2715';
const CHECK = '\u2713';
const ARROW = '\u2192';
const TRIUP = '\u25B2';
const TRIDN = '\u25BC';
const CIRC = '\u25CB';
const IC = { size: 18, strokeWidth: 1.75 };
const ICS = { size: 15, strokeWidth: 1.75 };

const ROSTER = ['Jaclyn','Tiffani','Taryn','Andrew','Aurora','Zac','Sam','Catherine','Cyndie','Peter','Chad','Allison'];
const SCRIPT = '"[Facility name], this is [your name], how may I help you?"';
// PLAYBOOK — preserved verbatim from the original page (the standard the Brain coaches against)
const PLAYBOOK = [
  { t: '1. THE GREETING', b: 'Every call, no exceptions: "[Facility name], this is [your name], how may I help you?" Identify your facility by its own brand. Give your name. Offer to help. The AI checks every call for all three.' },
  { t: '2. THE MINDSET', b: 'This caller may have 20 minutes of willingness. This call may be the only one they make. Connect first, sell second. You are not selling a bed \u2014 you are the person who answered when they finally asked for help.' },
  { t: '3. CONNECT (first 2 minutes)', b: 'Get their name and use it. Are they the patient or a loved one? Let them talk. Acknowledge what it took to call: "I\u2019m really glad you called." No forms, no interrogation yet.' },
  { t: '4. DISCOVER (weave it in, don\u2019t interrogate)', b: 'What\u2019s going on \u2014 substance(s), last use, how long, how much. Prior treatment: where, when, what happened. Mental health picture and SI/HI \u2014 handle with care. Medical concerns and detox risk. Insurance: carrier, member ID, DOB \u2014 "so I can check your coverage while we talk." Where they are, transport needs. HOW SOON \u2014 the money question. Who else is involved in the decision.' },
  { t: '5. EVERY CALL IS A GOOD CALL', b: 'Explore every option before the call ends. PRIVATE/COMMERCIAL insurance: verify benefits on the spot \u2014 takes minutes, costs nothing. CASH PAY: typically $22,500 for 30 days \u2014 but play the "what can you afford?" game; there is almost always a path. NO INSURANCE: don\u2019t stop. Ask about FAMILY first \u2014 exchange/ACA policies often run $50\u2013$1,000 a month, and most families can do that to save their loved one. Someone in crisis navigating the ACA site alone is hard; a family member doing it for them works. Or refer them to our resource who helps people get covered. MEDI-CAL / MEDICAID / MEDICARE: not a lost lead \u2014 it\u2019s a longer play toward off-exchange private coverage, and this team knows the game. The only truly dead call is the one where you didn\u2019t explore the options.' },
  { t: '6. CLOSE \u2014 NEVER END VAGUE', b: 'Every call ends with a concrete next step, strongest first: admit date > VOB submitted > prescreen scheduled > firm callback at a specific hour (not "sometime"). Say it back: "So here\u2019s what happens next\u2026"' },
  { t: '7. AFTER THE CALL', b: 'Log the outcome in My Queue. Fill every lead field you gathered. Request the VOB while it\u2019s hot.' },
];

// Metric registry: label, icon, accent, extractor, kind (pct | score10 | score5)
const METRICS = [
  { key: 'greeting_pct',     label: 'Greeting',          icon: Hand,          accent: tk.blue600, kind: 'pct' },
  { key: 'name_capture_pct', label: 'Name Capture',      icon: UserRound,     accent: tk.blue600, kind: 'pct' },
  { key: 'discovery_avg',    label: 'Discovery',         icon: MessagesSquare,accent: tk.teal,    kind: 'score10' },
  { key: 'insurance_pct',    label: 'Insurance Capture', icon: ShieldCheck,   accent: tk.teal,    kind: 'pct' },
  { key: 'next_step_pct',    label: 'Next Step Secured', icon: FlagIcon,      accent: tk.blue600, kind: 'pct' },
  { key: 'service_avg',      label: 'Service / Family',  icon: HeartHandshake,accent: tk.purple,  kind: 'score10' },
];

const fmtWhen = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
const fmtDur = (s) => { if (!s) return ''; const m = Math.floor(s / 60), sec = s % 60; return m + ':' + String(sec).padStart(2, '0'); };
const scoreColor = (n) => n == null ? tk.textMuted : n >= 7 ? tk.success : n >= 5 ? tk.warning : tk.danger;
const metricVal = (obj, m) => obj ? obj[m.key] : null;
const metricDisp = (v, kind) => v == null ? DASH : kind === 'pct' ? v + '%' : v;
// Deterministic grouping (ratified): >=70 strength; rising below 70 improving; else needs focus
const groupOf = (v, delta, kind) => {
  if (v == null) return null;
  const pct = kind === 'pct' ? v : v * 10;
  if (pct >= 70) return 'strength';
  if (delta != null && delta > 0) return 'improving';
  return 'focus';
};

function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' && window.innerWidth < 700);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 700);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

function TrendChip({ cur, prior, kind, goodUp = true }) {
  if (cur == null || prior == null) return null;
  const d = kind === 'pct' ? cur - prior : +(cur - prior).toFixed(1);
  if (!d) return null;
  const up = d > 0;
  const good = up === goodUp;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, fontWeight: 700, color: good ? tk.success : tk.danger, fontVariantNumeric: 'tabular-nums' }}>
      {up ? TRIUP : TRIDN} {Math.abs(d)}{kind === 'pct' ? '%' : ''}
    </span>
  );
}

function KpiTile({ metric, cur, prior }) {
  const Icon = metric.icon;
  const v = metricVal(cur, metric);
  const pv = metricVal(prior, metric);
  return (
    <div style={{ background: tk.surface, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusCard, padding: '11px 14px', minWidth: 118, boxShadow: tk.shadow }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <Icon {...ICS} color={metric.accent} />
        <span style={{ fontSize: 10.5, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{metric.label}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 21, fontWeight: 700, color: tk.ink, fontVariantNumeric: 'tabular-nums' }}>{metricDisp(v, metric.kind)}</span>
        <TrendChip cur={v} prior={pv} kind={metric.kind} />
      </div>
    </div>
  );
}

function MetricBar({ label, icon: Icon, value, delta, kind, accent }) {
  const pct = value == null ? 0 : Math.min(100, kind === 'pct' ? value : value * 10);
  return (
    <div style={{ marginBottom: 11 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: tk.ink }}>
          {Icon && <Icon {...ICS} color={accent} />}{label}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: tk.ink, fontVariantNumeric: 'tabular-nums' }}>
          {metricDisp(value, kind)}
          {delta != null && delta !== 0 && (
            <span style={{ fontSize: 10.5, fontWeight: 700, color: delta > 0 ? tk.success : tk.danger }}>
              {delta > 0 ? TRIUP : TRIDN}{Math.abs(delta)}{kind === 'pct' ? '%' : ''}
            </span>
          )}
        </span>
      </div>
      <div style={{ height: 6, background: tk.surfaceSoft, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: accent, borderRadius: 3, transition: 'width 200ms' }} />
      </div>
    </div>
  );
}

function InsightCard({ icon: Icon, accent, softBg, title, body, count, action, onAction }) {
  return (
    <div style={{ flex: '1 1 260px', minWidth: 240, background: tk.surface, border: `1px solid ${tk.borderSoft}`, borderLeft: `3px solid ${accent}`, borderRadius: tk.radiusCard, padding: '12px 15px', boxShadow: tk.shadow }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
        <Icon {...IC} color={accent} />
        <span style={{ fontSize: 13, fontWeight: 700, color: tk.ink }}>{title}</span>
      </div>
      <div style={{ fontSize: 12.5, color: tk.textSecondary, lineHeight: 1.55, marginBottom: 6 }}>{body}</div>
      {count != null && <div style={{ fontSize: 11.5, fontWeight: 700, color: accent, marginBottom: 8 }}>{count}</div>}
      {action && (
        <button onClick={onAction} style={{ padding: '5px 12px', background: tk.surface, color: tk.ai, border: `1px solid ${tk.ai}`, borderRadius: tk.radiusControl, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          {action} {ARROW}
        </button>
      )}
    </div>
  );
}

function MissTag({ label }) {
  return <span style={{ fontSize: 10, fontWeight: 700, background: tk.dangerBg, color: tk.danger, border: `1px solid ${tk.danger}`, padding: '1px 7px', borderRadius: 6, letterSpacing: '0.03em', whiteSpace: 'nowrap' }}>{label}</span>;
}
function OkTag({ label }) {
  return <span style={{ fontSize: 10, fontWeight: 700, background: tk.successBg, color: tk.success, border: `1px solid ${tk.success}`, padding: '1px 7px', borderRadius: 6, letterSpacing: '0.03em', whiteSpace: 'nowrap' }}>{label}</span>;
}
const missesOf = (c) => {
  const m = [];
  if (c.greeting_compliant === false) m.push('NO GREETING');
  if (c.got_caller_name === false) m.push('NO NAME CAPTURE');
  if (c.discovery_score != null && c.discovery_score <= 3) m.push('WEAK DISCOVERY');
  if (c.next_step_secured === false) m.push('NO NEXT STEP');
  return m;
};
const stageLabel = (c) => {
  if (!c.lead_id) return null;
  const s = c.lead_stage;
  if (s === 'admitted') return { label: 'Admitted', color: tk.success };
  if (s === 'pending_admit') return { label: 'Pending Admit', color: tk.warning };
  if (s === 'lost' || s === 'not_viable') return { label: 'Lost', color: tk.danger };
  return { label: s ? s.replace(/_/g, ' ') : 'Lead', color: tk.blue600 };
};

// ─── REP COACHING PROFILE — Strengths / Improving / Needs Focus (no radar, ratified) ───
function RepProfile({ reps, faith, faithPrior }) {
  const opts = reps.map(r => r.rep_name);
  const [sel, setSel] = useState(opts[0] || 'FAITH');
  useEffect(() => { if (sel !== 'FAITH' && opts.length && !opts.includes(sel)) setSel(opts[0]); }, [opts, sel]);
  const isFaith = sel === 'FAITH';
  const rep = isFaith ? faith : reps.find(r => r.rep_name === sel);
  const prior = isFaith ? faithPrior : rep?.prior;
  if (!rep) return null;
  const rows = METRICS.map(m => {
    const v = metricVal(rep, m);
    const pv = prior ? metricVal(prior, m) : null;
    const delta = (v != null && pv != null) ? (m.kind === 'pct' ? v - pv : +(v - pv).toFixed(1)) : null;
    return { m, v, delta, group: groupOf(v, delta, m.kind) };
  }).filter(r => r.v != null);
  const groups = [
    { key: 'strength', title: 'STRENGTHS', color: tk.success, icon: TrendingUp },
    { key: 'improving', title: 'IMPROVING', color: tk.warning, icon: TrendingUp },
    { key: 'focus', title: 'NEEDS FOCUS', color: tk.danger, icon: Target },
  ];
  return (
    <div style={{ background: tk.surface, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusCard, boxShadow: tk.shadow, padding: '14px 18px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, color: tk.ink, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          <UserRound {...IC} color={tk.blue600} />Rep Coaching Profile
        </span>
        <select value={sel} onChange={e => setSel(e.target.value)}
          style={{ padding: '7px 11px', borderRadius: tk.radiusControl, border: `1px solid ${tk.border}`, fontSize: 12.5, fontWeight: 600, color: tk.ink, cursor: 'pointer', background: tk.surface, fontFamily: 'inherit' }}>
          {opts.map(n => <option key={n} value={n}>{n} ({reps.find(r => r.rep_name === n)?.calls} calls)</option>)}
          {faith && faith.calls > 0 && <option value="FAITH">Faith (AI) {DOT} {faith.calls} calls</option>}
        </select>
      </div>
      {isFaith && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, background: tk.purpleSoft, border: `1px solid ${tk.purple}`, borderRadius: tk.radiusControl, padding: '7px 12px', marginBottom: 12, fontSize: 12, color: tk.purple, fontWeight: 600 }}>
          <Bot {...ICS} color={tk.purple} />AI receptionist {DOT} scored on the same playbook {DOT} excluded from team averages and Coach This Week
        </div>
      )}
      {rows.length === 0 ? (
        <div style={{ fontSize: 12.5, color: tk.textMuted }}>Not enough scored calls in this window.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18 }}>
          {groups.map(g => {
            const items = rows.filter(r => r.group === g.key);
            if (!items.length) return null;
            return (
              <div key={g.key}>
                <div style={{ fontSize: 11, fontWeight: 700, color: g.color, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>{g.title}</div>
                {items.map(r => (
                  <MetricBar key={r.m.key} label={r.m.label} icon={r.m.icon} value={r.v} delta={r.delta} kind={r.m.kind}
                    accent={g.key === 'strength' ? tk.success : g.key === 'improving' ? tk.warning : tk.danger} />
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── COACH THIS WEEK v0 — deterministic + explainable (ratified: 10-call floor, real math only) ───
function CoachThisWeek({ reps, onReview }) {
  const FLOOR = 10;
  const cands = [];
  reps.filter(r => r.seeker_calls >= FLOOR).forEach(r => {
    METRICS.filter(m => m.kind === 'pct' && m.key !== 'insurance_pct').forEach(m => {
      const v = metricVal(r, m);
      if (v != null && v < 60) cands.push({ rep: r.rep_name, metric: m, v, calls: r.seeker_calls, sev: (60 - v) * r.seeker_calls });
    });
  });
  const seen = new Set();
  const picks = cands.sort((a, b) => b.sev - a.sev).filter(c => !seen.has(c.rep) && seen.add(c.rep)).slice(0, 4);
  return (
    <div style={{ background: tk.surface, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusCard, boxShadow: tk.shadow, padding: '14px 18px' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, color: tk.ink, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
        <Target {...IC} color={tk.purple} />Coach This Week
      </div>
      <div style={{ fontSize: 11, color: tk.textMuted, marginBottom: 12 }}>Lowest playbook compliance {DOT} reps with {FLOOR}+ seeker calls this window {DOT} priority = gap {TIMES} volume</div>
      {picks.length === 0 ? (
        <div style={{ fontSize: 12.5, color: tk.textMuted }}>No rep clears the {FLOOR}-call floor with a category under 60% this window.</div>
      ) : picks.map((p, i) => (
        <div key={p.rep} style={{ padding: '10px 0', borderTop: i ? `1px solid ${tk.borderSoft}` : 'none' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: tk.ink }}>{p.rep}</span>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: i === 0 ? tk.dangerBg : tk.warningBg, color: i === 0 ? tk.danger : tk.warning }}>
              {i === 0 ? 'HIGH PRIORITY' : 'MEDIUM'}
            </span>
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: tk.textSecondary, marginTop: 2 }}>{p.metric.label}</div>
          <div style={{ fontSize: 11.5, color: tk.textMuted, fontVariantNumeric: 'tabular-nums' }}>{p.calls} seeker calls {DOT} {p.v}% compliant</div>
          <button onClick={() => onReview(p.rep)} style={{ marginTop: 6, padding: '4px 11px', background: tk.surface, color: tk.ai, border: `1px solid ${tk.ai}`, borderRadius: tk.radiusControl, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          Review Calls {ARROW}</button>
        </div>
      ))}
    </div>
  );
}

// ─── TRANSCRIPT — diarized bubbles from "Caller: / Agent:" turns; source never altered ───
function TranscriptView({ transcript }) {
  if (!transcript) return <div style={{ fontSize: 12.5, color: tk.textMuted }}>No transcript available.</div>;
  const turns = [];
  const re = /(Caller|Agent):\s*/g;
  let m, last = null, lastIdx = 0;
  while ((m = re.exec(transcript)) !== null) {
    if (last) turns.push({ who: last, text: transcript.slice(lastIdx, m.index).trim() });
    last = m[1]; lastIdx = re.lastIndex;
  }
  if (last) turns.push({ who: last, text: transcript.slice(lastIdx).trim() });
  if (!turns.length) return <div style={{ fontSize: 13, color: tk.ink, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{transcript}</div>;
  return (
    <div>
      {turns.map((t, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: t.who === 'Agent' ? 'flex-end' : 'flex-start', marginBottom: 7 }}>
          <div style={{ maxWidth: '78%', padding: '7px 12px', borderRadius: 10, fontSize: 12.5, lineHeight: 1.55,
            background: t.who === 'Agent' ? tk.aiSoft : tk.surfaceSoft, color: tk.ink,
            border: `1px solid ${t.who === 'Agent' ? tk.blue400 : tk.borderSoft}` }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: t.who === 'Agent' ? tk.blue700 : tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{t.who === 'Agent' ? 'Rep' : 'Caller'}</div>
            {t.text}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── CALL REVIEW MODAL — centered (ratified), 5 tabs, one fetch ───
function ReviewModal({ callrailId, isAdmin, token, onClose, onAssign }) {
  const [d, setD] = useState(null);
  const [journey, setJourney] = useState(null);
  const [notes, setNotes] = useState(null);
  const [tab, setTab] = useState('summary');
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const hdr = { headers: { Authorization: `Bearer ${token}` } };
  useEffect(() => {
    fetch(`${API}/api/coach/${callrailId}`, hdr).then(r => r.json()).then(j => { if (j.ok) setD(j.data); });
    fetch(`${API}/api/coach/${callrailId}/journey`, hdr).then(r => r.json()).then(j => { if (j.ok) setJourney(j.data); });
    fetch(`${API}/api/coach/${callrailId}/notes`, hdr).then(r => r.json()).then(j => { if (j.ok) setNotes(j.data); });
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callrailId]);
  const saveNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      const r = await fetch(`${API}/api/coach/${callrailId}/notes`, { method: 'POST', headers: { ...hdr.headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ note: noteText, rep_name: d?.rep_name || null }) });
      const j = await r.json();
      if (j.ok) { setNotes(n => [j.data, ...(n || [])]); setNoteText(''); }
      else alert(j.error || 'Save failed');
    } catch (e) { alert('Network error'); }
    setSavingNote(false);
  };
  const sec = (title, color, text) => text ? (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: tk.ink, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{text}</div>
    </div>
  ) : null;
  const tabs = [['summary','Summary'],['playbook','Playbook'],['transcript','Transcript'],['journey','Journey'],['notes','Notes']];
  const jStep = (label, done) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 76 }}>
      <span style={{ width: 26, height: 26, borderRadius: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
        background: done ? tk.success : tk.surfaceSoft, color: done ? '#fff' : tk.textMuted, border: done ? 'none' : `1px solid ${tk.border}` }}>
        {done ? CHECK : CIRC}
      </span>
      <span style={{ fontSize: 10.5, fontWeight: 600, color: done ? tk.ink : tk.textMuted, textAlign: 'center' }}>{label}</span>
    </div>
  );
  const flagRow = (label, ok, evidence) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${tk.borderSoft}` }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: tk.ink }}>{label}</span>
      {ok === null || ok === undefined
        ? <span style={{ fontSize: 11, fontWeight: 700, color: tk.textMuted }}>N/A</span>
        : ok === true ? <OkTag label={'MET ' + CHECK} /> : ok === 'partial' ? <span style={{ fontSize: 10, fontWeight: 700, background: tk.warningBg, color: tk.warning, border: `1px solid ${tk.warning}`, padding: '1px 7px', borderRadius: 6 }}>PARTIAL</span> : <MissTag label="MISSED" />}
    </div>
  );
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,34,57,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: tk.surface, borderRadius: tk.radiusCard, width: 'min(1000px, 96vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(10,34,57,0.3)', fontFamily: tk.font }}>
        {!d ? <div style={{ padding: 60, textAlign: 'center', color: tk.textMuted }}>Loading{ELLIP}</div> : (
          <>
            <div style={{ padding: '16px 22px 0', borderBottom: `1px solid ${tk.borderSoft}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ minWidth: 42, height: 42, borderRadius: 10, background: scoreColor(d.service_score ?? d.score), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{d.service_score ?? d.score ?? DASH}</span>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 16, fontWeight: 700, color: tk.ink }}>{(d.extracted && (d.extracted.first_name || d.extracted.last_name)) ? [d.extracted.first_name, d.extracted.last_name].filter(Boolean).join(' ') : (d.customer_phone || 'Unknown caller')}</span>
                      {d.ai_handled ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700, color: tk.purple }}><Bot {...ICS} color={tk.purple} />FAITH (AI)</span>
                        : d.rep_name ? <span style={{ fontSize: 12.5, fontWeight: 600, color: tk.textSecondary }}>{DOT} {d.rep_name}</span>
                        : isAdmin ? (
                          <select defaultValue="" onChange={e => { if (e.target.value) { onAssign(callrailId, e.target.value); setD(prev => ({ ...prev, rep_name: e.target.value })); } }}
                            style={{ fontSize: 11.5, padding: '3px 7px', borderRadius: 6, border: `1px solid ${tk.danger}`, color: tk.danger, background: tk.dangerBg, fontWeight: 700, fontFamily: 'inherit' }}>
                            <option value="">Unknown {DASH} assign rep{ELLIP}</option>
                            {ROSTER.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        ) : null}
                    </div>
                    <div style={{ fontSize: 12, color: tk.textSecondary, marginTop: 2 }}>
                      {fmtWhen(d.call_started_at)} {DOT} {fmtDur(d.duration_seconds)}{d.call_type ? ' ' + DOT + ' ' + d.call_type.replace(/_/g, ' ') : ''}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {d.recording_url && (
                    <audio controls preload="none" src={d.recording_url} style={{ height: 34, maxWidth: 280 }} />
                  )}
                  <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: tk.textMuted, padding: 4 }}><X {...IC} color={tk.textMuted} /></button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', margin: '10px 0' }}>
                {missesOf(d).map(t => <MissTag key={t} label={t} />)}
                {d.urgency_flag && <span style={{ fontSize: 10, fontWeight: 700, background: tk.danger, color: '#fff', padding: '1px 7px', borderRadius: 6 }}>URGENT</span>}
              </div>
              <div style={{ display: 'flex', gap: 2 }}>
                {tabs.map(([k, lbl]) => (
                  <button key={k} onClick={() => setTab(k)}
                    style={{ padding: '8px 14px', background: 'none', border: 'none', borderBottom: tab === k ? `2px solid ${tk.ai}` : '2px solid transparent', color: tab === k ? tk.ai : tk.textSecondary, fontSize: 13, fontWeight: tab === k ? 700 : 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ padding: '18px 22px', overflowY: 'auto', flex: 1 }}>
              {tab === 'summary' && (
                <>
                  {sec('Call Summary', tk.textMuted, d.summary)}
                  {sec(CHECK + ' What Worked', tk.success, d.strengths)}
                  {sec('\u25B3 Missed Opportunities', tk.warning, [d.weaknesses, d.missed_opportunities].filter(Boolean).join('\n\n'))}
                  {d.top_fixes && (
                    <div style={{ background: tk.purpleSoft, border: `1px solid ${tk.purple}`, borderRadius: tk.radiusControl, padding: '12px 15px' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: tk.purple, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 }}>
                        <Sparkles {...ICS} color={tk.purple} />Next Time, Say This
                      </div>
                      <div style={{ fontSize: 13, color: tk.ink, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{d.top_fixes}</div>
                    </div>
                  )}
                </>
              )}
              {tab === 'playbook' && (
                <>
                  <div style={{ fontSize: 11.5, color: tk.textMuted, marginBottom: 10 }}>Scored playbook checks from this call {DOT} sections without an automated check show N/A</div>
                  {flagRow('1. Greeting (facility + name + offer to help)', d.greeting_compliant)}
                  {flagRow('3. Connect \u2014 got the caller\u2019s name', d.got_caller_name)}
                  {flagRow('4. Discover (0-10: ' + (d.discovery_score ?? DASH) + ')', d.discovery_score == null ? null : d.discovery_score >= 7 ? true : d.discovery_score >= 4 ? 'partial' : false)}
                  {flagRow('4. Insurance captured', d.extracted ? !!d.extracted.payer_name : null)}
                  {flagRow('6. Close \u2014 concrete next step', d.next_step_secured)}
                  <div style={{ marginTop: 16 }}>
                    {PLAYBOOK.map(p => (
                      <details key={p.t} style={{ marginBottom: 8 }}>
                        <summary style={{ fontSize: 12, fontWeight: 700, color: tk.blue700, cursor: 'pointer' }}>{p.t}</summary>
                        <div style={{ fontSize: 12.5, color: tk.textSecondary, lineHeight: 1.6, padding: '6px 0 0 14px' }}>{p.b}</div>
                      </details>
                    ))}
                  </div>
                </>
              )}
              {tab === 'transcript' && <TranscriptView transcript={d.transcript} />}
              {tab === 'journey' && (
                journey == null ? <div style={{ fontSize: 12.5, color: tk.textMuted }}>Loading{ELLIP}</div>
                : !journey.linked ? <div style={{ fontSize: 12.5, color: tk.textMuted }}>This call is not linked to a lead {DASH} no journey to show.</div>
                : (
                  <>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 16 }}>
                      {jStep('Lead Created', true)}
                      {jStep('VOB', journey.vob_requested)}
                      {jStep('Prescreen', journey.prescreen_done)}
                      {jStep('Pending Admit', journey.pending_admit)}
                      {jStep('Admitted', journey.admitted || journey.pipeline_stage === 'admitted')}
                    </div>
                    <div style={{ fontSize: 13, color: tk.ink }}>
                      Current stage: <b>{(journey.pipeline_stage || DASH).replace(/_/g, ' ')}</b>
                      {journey.lost_reason && <span style={{ color: tk.danger }}> {DOT} {journey.lost_reason}</span>}
                    </div>
                    <a href={`#/lead/${journey.id}`} style={{ display: 'inline-block', marginTop: 10, fontSize: 12, fontWeight: 700, color: tk.ai, textDecoration: 'none' }}>Open Lead {ARROW}</a>
                  </>
                )
              )}
              {tab === 'notes' && (
                <>
                  {isAdmin && (
                    <div style={{ marginBottom: 14 }}>
                      <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder={'Coaching note for this call' + ELLIP}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: `1.5px solid ${tk.border}`, borderRadius: tk.radiusControl, fontSize: 13, fontFamily: 'inherit', minHeight: 70, resize: 'vertical', color: tk.ink }} />
                      <button onClick={saveNote} disabled={savingNote || !noteText.trim()}
                        style={{ marginTop: 6, padding: '7px 16px', background: tk.ai, color: '#fff', border: 'none', borderRadius: tk.radiusControl, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: savingNote || !noteText.trim() ? 0.6 : 1 }}>
                        {savingNote ? 'Saving' + ELLIP : 'Create Coaching Note'}
                      </button>
                    </div>
                  )}
                  {notes == null ? <div style={{ fontSize: 12.5, color: tk.textMuted }}>Loading{ELLIP}</div>
                    : notes.length === 0 ? <div style={{ fontSize: 12.5, color: tk.textMuted }}>No coaching notes yet.</div>
                    : notes.map(n => (
                      <div key={n.id} style={{ background: tk.surfaceSoft, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusControl, padding: '10px 13px', marginBottom: 8 }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: tk.textMuted, marginBottom: 4 }}>
                          <StickyNote {...ICS} color={tk.textMuted} />{n.author || 'Unknown'} {DOT} {fmtWhen(n.created_at)}
                        </div>
                        <div style={{ fontSize: 13, color: tk.ink, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{n.note}</div>
                      </div>
                    ))}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── MAIN COACHING PAGE ───────────────────────────────────────
export default function Coaching({ user }) {
  const isMobile = useIsMobile();
  const token = user?.token || localStorage.getItem('tcio_token');
  const [d, setD] = useState(null);
  const [days, setDays] = useState(30);
  const [showPlaybook, setShowPlaybook] = useState(false);
  const [filter, setFilter] = useState('all');
  const [repFilter, setRepFilter] = useState('');
  const [search, setSearch] = useState('');
  const [openCall, setOpenCall] = useState(null);
  const [showAllCalls, setShowAllCalls] = useState(false);

  const fetchData = useCallback(() => {
    fetch(`${API}/api/coach?days=${days}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(j => { if (j.ok) setD(j); });
  }, [token, days]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const assignRep = async (callrailId, rep) => {
    await fetch(`${API}/api/coach/${callrailId}/assign`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ rep_name: rep }),
    });
    setD(prev => prev ? { ...prev, data: prev.data.map(x => x.callrail_id === callrailId ? { ...x, rep_name: rep } : x) } : prev);
  };

  if (!d) return <div style={{ padding: 60, textAlign: 'center', color: tk.textMuted, fontFamily: tk.font }}>Loading{ELLIP}</div>;
  const S = d.summary || {};
  const P = d.prior || {};
  const calls = d.data || [];

  // Queue filters — real classifications only (ratified)
  const passes = (c) => {
    if (filter === 'needs' && !(c.score != null && c.score < 5 && c.call_type === 'NEW_SEEKER')) return false;
    if (filter === 'urgent' && !c.urgency_flag) return false;
    if (filter === 'great' && !(c.score != null && c.score >= 8)) return false;
    if (filter === 'faith' && !c.ai_handled) return false;
    if (filter === 'service' && c.service_score == null) return false;
    if (filter === 'junk' && !['TOO_SHORT', 'JUNK', 'OTHER_BUSINESS'].includes(c.call_type)) return false;
    if (filter === 'all' && ['TOO_SHORT', 'JUNK'].includes(c.call_type)) return false;
    if (repFilter && (c.rep_name || '') !== repFilter) return false;
    if (search) {
      const hay = ((c.rep_name || '') + ' ' + (c.customer_phone || '') + ' ' + (c.extracted ? (c.extracted.first_name || '') + ' ' + (c.extracted.last_name || '') : '') + ' ' + (c.summary || '')).toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  };
  const shown = calls.filter(passes);
  const PAGE = 25;
  const visible = showAllCalls ? shown : shown.slice(0, PAGE);

  const chips = [
    ['all', 'All Calls', calls.filter(c => !['TOO_SHORT','JUNK'].includes(c.call_type)).length],
    ['needs', 'Needs Coaching', calls.filter(c => c.score != null && c.score < 5 && c.call_type === 'NEW_SEEKER').length],
    ['urgent', 'Urgent', calls.filter(c => c.urgency_flag).length],
    ['great', 'Great Calls', calls.filter(c => c.score != null && c.score >= 8).length],
    ['service', 'Service Calls', calls.filter(c => c.service_score != null).length],
    ['faith', 'Faith (AI)', calls.filter(c => c.ai_handled).length],
    ['junk', 'Junk/Short', calls.filter(c => ['TOO_SHORT','JUNK','OTHER_BUSINESS'].includes(c.call_type)).length],
  ];

  const callerName = (c) => (c.extracted && (c.extracted.first_name || c.extracted.last_name))
    ? [c.extracted.first_name, c.extracted.last_name].filter(Boolean).join(' ') : (c.customer_phone || 'Unknown');

  const collapsedRow = (c) => {
    const misses = missesOf(c);
    const st = stageLabel(c);
    return (
      <div key={c.callrail_id} onClick={() => setOpenCall(c.callrail_id)}
        style={{ display: 'grid', gridTemplateColumns: '52px 1fr 1fr 0.9fr 0.5fr 0.8fr 1.6fr 0.6fr', gap: 8, alignItems: 'center', padding: '10px 14px', borderTop: `1px solid ${tk.borderSoft}`, cursor: 'pointer', fontSize: 12.5, color: tk.ink, transition: 'background 160ms' }}
        onMouseEnter={e => e.currentTarget.style.background = tk.surfaceSoft}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        <span style={{ width: 36, height: 26, borderRadius: 7, background: scoreColor(c.service_score ?? c.score), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{c.service_score ?? c.score ?? DASH}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontWeight: 700, minWidth: 0 }}>
          {c.ai_handled && <Bot {...ICS} color={tk.purple} />}
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.ai_handled ? 'Faith' : (c.rep_name || <span style={{ color: tk.danger }}>Unassigned</span>)}</span>
        </span>
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: tk.textSecondary }}>{callerName(c)}</span>
        <span style={{ color: tk.textSecondary, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtWhen(c.call_started_at)}</span>
        <span style={{ fontVariantNumeric: 'tabular-nums', color: tk.textSecondary }}>{fmtDur(c.duration_seconds)}</span>
        <span>{st ? <span style={{ fontSize: 11, fontWeight: 700, color: st.color }}>{st.label}</span> : <span style={{ color: tk.textMuted }}>{DASH}</span>}</span>
        <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{misses.slice(0, 3).map(t => <MissTag key={t} label={t} />)}{misses.length === 0 && c.score >= 8 && <OkTag label={'GREAT CALL'} />}</span>
        <button onClick={e => { e.stopPropagation(); setOpenCall(c.callrail_id); }}
          style={{ padding: '5px 12px', background: tk.ai, color: '#fff', border: 'none', borderRadius: tk.radiusControl, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Review</button>
      </div>
    );
  };

  const mobileCard = (c) => {
    const misses = missesOf(c);
    return (
      <div key={c.callrail_id} onClick={() => setOpenCall(c.callrail_id)}
        style={{ border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusControl, padding: '10px 12px', marginBottom: 8, cursor: 'pointer', background: tk.surface }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ width: 32, height: 24, borderRadius: 6, background: scoreColor(c.service_score ?? c.score), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>{c.service_score ?? c.score ?? DASH}</span>
            <b style={{ fontSize: 13, color: tk.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.ai_handled ? 'Faith' : (c.rep_name || 'Unassigned')} {DOT} {callerName(c)}</b>
          </span>
          <span style={{ fontSize: 11, color: tk.textMuted, whiteSpace: 'nowrap' }}>{fmtDur(c.duration_seconds)}</span>
        </div>
        <div style={{ fontSize: 11, color: tk.textSecondary, marginTop: 4 }}>{fmtWhen(c.call_started_at)}</div>
        {misses.length > 0 && <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>{misses.map(t => <MissTag key={t} label={t} />)}</div>}
      </div>
    );
  };

  return (
    <div style={{ padding: isMobile ? '16px 12px' : '24px 32px', maxWidth: 1440, margin: '0 auto', fontFamily: tk.font, background: tk.canvas, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: '0 0 2px', fontSize: 24, fontWeight: 700, color: tk.ink, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            Call Coaching <Sparkles {...IC} color={tk.purple} />
          </h2>
          <div style={{ fontSize: 13, color: tk.textSecondary }}>
            {d.is_admin ? 'Team coaching view ' + DASH + ' every call reviewed so every rep gets better.' : 'Your personal call coach ' + DASH + ' every call reviewed, so every call makes you better.'}
          </div>
          {d.is_admin && (d.awaiting_scoring > 0 || d.unassigned > 0) && (
            <div style={{ fontSize: 11.5, fontWeight: 600, color: tk.warning, marginTop: 4 }}>
              {d.awaiting_scoring > 0 && `${d.awaiting_scoring} transcribed calls awaiting scoring (auto-scores every 15 min)`}
              {d.awaiting_scoring > 0 && d.unassigned > 0 && ' ' + DOT + ' '}
              {d.unassigned > 0 && `${d.unassigned} scored calls need a rep assigned`}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', border: `1px solid ${tk.border}`, borderRadius: tk.radiusControl, overflow: 'hidden' }}>
            {[7, 30, 90].map(w => (
              <button key={w} onClick={() => setDays(w)}
                style={{ padding: '7px 13px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: 'none', borderLeft: w !== 7 ? `1px solid ${tk.border}` : 'none', background: days === w ? tk.aiSoft : tk.surface, color: days === w ? tk.ai : tk.textSecondary }}>
                {w} Days
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Playbook — collapsed by default (ratified section 2) */}
      <div style={{ background: tk.navy900, borderRadius: tk.radiusCard, padding: '14px 20px', marginBottom: 14, cursor: 'pointer' }} onClick={() => setShowPlaybook(p => !p)}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <BookOpen {...IC} color={tk.blue400} />
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: tk.blue400, letterSpacing: '0.08em' }}>COACHING STANDARD {DOT} THE PLAYBOOK</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{SCRIPT}</div>
            </div>
          </div>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: tk.blue400, whiteSpace: 'nowrap' }}>{showPlaybook ? 'Close' : 'View Playbook'} {showPlaybook ? TRIUP : TRIDN}</span>
        </div>
        {showPlaybook && (
          <div style={{ marginTop: 14, borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 12 }} onClick={e => e.stopPropagation()}>
            {PLAYBOOK.map(p => (
              <div key={p.t} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: tk.blue400, letterSpacing: '0.06em', marginBottom: 3 }}>{p.t}</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.92)', lineHeight: 1.6 }}>{p.b}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Team snapshot — real numbers, real trends */}
      <div style={{ fontSize: 12, fontWeight: 700, color: tk.ink, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Team Coaching Snapshot <span style={{ fontWeight: 600, color: tk.textMuted, textTransform: 'none', letterSpacing: 0 }}>{DOT} {S.seeker_calls || 0} seeker calls {DOT} vs prior {days} days</span></div>
      <div style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? '1fr 1fr' : undefined, gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ background: tk.surface, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusCard, padding: '11px 14px', minWidth: 118, boxShadow: tk.shadow }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <AudioLines {...ICS} color={tk.ai} />
            <span style={{ fontSize: 10.5, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Overall Score</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 21, fontWeight: 700, color: scoreColor(S.avg_score), fontVariantNumeric: 'tabular-nums' }}>{S.avg_score ?? DASH}<span style={{ fontSize: 12, color: tk.textMuted, fontWeight: 600 }}> / 10</span></span>
            <TrendChip cur={S.avg_score} prior={P.avg_score} kind="score10" />
          </div>
        </div>
        {METRICS.map(m => <KpiTile key={m.key} metric={m} cur={S} prior={P} />)}
      </div>

      {/* Insights — honest set only (A-class aggregates; more slots fill as Phase 2 lands) */}
      {d.is_admin && S.seeker_calls > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          {S.greeting_pct != null && S.greeting_pct < 70 && (
            <InsightCard icon={Hand} accent={tk.danger} title={'Greeting is the team\u2019s biggest basic failure'}
              body={`Only ${S.greeting_pct}% of reviewed seeker calls used the approved greeting.`}
              count={`${Math.round(S.seeker_calls * (100 - S.greeting_pct) / 100)} calls affected`}
              action="Review Calls" onAction={() => { setFilter('needs'); }} />
          )}
          {S.next_step_pct != null && S.next_step_pct < 60 && (
            <InsightCard icon={FlagIcon} accent={tk.warning} title="Next-step discipline is weak"
              body={`${100 - S.next_step_pct}% of reviewed seeker calls ended without a concrete next action.`}
              action="Review Calls" onAction={() => { setFilter('needs'); }} />
          )}
          {S.insurance_pct != null && (
            <InsightCard icon={ShieldCheck} accent={tk.teal} title="Insurance capture on first calls"
              body={`${S.insurance_pct}% of seeker calls captured a payer name in-call ` + DASH + ' the raw material for same-day VOBs.'}
              action="Review Calls" onAction={() => { setFilter('all'); }} />
          )}
        </div>
      )}

      {/* Rep profile + Coach This Week */}
      {d.is_admin && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.7fr 1fr', gap: 14, marginBottom: 16 }}>
          <RepProfile reps={d.reps || []} faith={d.faith} faithPrior={d.faith_prior} />
          <CoachThisWeek reps={d.reps || []} onReview={(rep) => { setRepFilter(rep); setFilter('all'); }} />
        </div>
      )}

      {/* Calls to review — dense queue */}
      <div style={{ background: tk.surface, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusCard, boxShadow: tk.shadow, overflow: 'hidden' }}>
        <div style={{ padding: '13px 16px 10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, color: tk.ink, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              <AudioLines {...IC} color={tk.blue600} />Calls to Review
            </span>
            {repFilter && (
              <button onClick={() => setRepFilter('')} style={{ padding: '4px 11px', background: tk.aiSoft, color: tk.ai, border: `1px solid ${tk.ai}`, borderRadius: tk.radiusControl, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                {repFilter} {TIMES}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            {chips.map(([k, lbl, n]) => (
              <button key={k} onClick={() => { setFilter(k); setShowAllCalls(false); }}
                style={{ padding: '5px 11px', borderRadius: tk.radiusControl, cursor: 'pointer', fontSize: 12, fontWeight: filter === k ? 700 : 600, fontFamily: 'inherit', background: filter === k ? tk.aiSoft : tk.surface, color: filter === k ? tk.ai : tk.textSecondary, border: `1px solid ${filter === k ? tk.ai : tk.borderSoft}` }}>
                {lbl} <b style={{ fontVariantNumeric: 'tabular-nums' }}>{n}</b>
              </button>
            ))}
          </div>
          <div style={{ position: 'relative', maxWidth: 420 }}>
            <Search {...ICS} color={tk.textMuted} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
            <input placeholder={'Search rep, caller, phone, or summary' + ELLIP} value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 11px 8px 34px', border: `1.5px solid ${tk.border}`, borderRadius: tk.radiusControl, fontSize: 13, fontFamily: 'inherit', color: tk.ink, outline: 'none' }} />
          </div>
        </div>
        {shown.length === 0 ? (
          <div style={{ padding: '20px 16px', fontSize: 13, color: tk.textMuted, borderTop: `1px solid ${tk.borderSoft}` }}>No calls match this filter.</div>
        ) : isMobile ? (
          <div style={{ padding: '4px 12px 12px' }}>{visible.map(mobileCard)}</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '52px 1fr 1fr 0.9fr 0.5fr 0.8fr 1.6fr 0.6fr', gap: 8, padding: '8px 14px', background: tk.surfaceSoft, fontSize: 10.5, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              <span>Score</span><span>Rep</span><span>Caller</span><span>Date / Time</span><span>Dur</span><span>Outcome</span><span>Key Misses</span><span></span>
            </div>
            {visible.map(collapsedRow)}
          </>
        )}
        {shown.length > PAGE && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 14px', borderTop: `1px solid ${tk.borderSoft}` }}>
            <button onClick={() => setShowAllCalls(s => !s)} style={{ padding: '6px 14px', background: tk.surface, color: tk.blue700, border: `1px solid ${tk.blue600}`, borderRadius: tk.radiusControl, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              {showAllCalls ? 'Show first ' + PAGE : `View All ${shown.length}`}
            </button>
          </div>
        )}
      </div>

      {openCall && <ReviewModal callrailId={openCall} isAdmin={d.is_admin} token={token} onClose={() => setOpenCall(null)} onAssign={assignRep} />}
    </div>
  );
}
